import { ThresholdCategory, Thresholds } from '@core/models/threshold.model';
import { fromCore } from '@core/store';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DataChartHelper } from '../helpers/data-chart.helper';
import { BlockGraph, ClosedPath, GraphConfig } from '../models/graph.model';
import { PathElement, PathEquipment } from '../models/path-element.model';
import { PathGasBlock } from '../models/path-gas-block.model';

import { State, gasBlockFeatureKey } from './gas-block.reducer';
import { FavoritePath } from '../models/favorite-path.model';
import { PartData } from '@shared/gui/models/part-data.model';
import { User } from '@core/models/user.model';
import { DateTime } from 'luxon';

const selectGasBlockState = createFeatureSelector<State>(gasBlockFeatureKey);

// Raw selectors

export const getDisplayMode = createSelector(selectGasBlockState, (state: State) => state.display);

export const getPathKeys = createSelector(selectGasBlockState, (state: State) => state.pathKeys);

export const getBlockedKeys = createSelector(selectGasBlockState, (state: State) => state.closedPaths);

export const getElements = createSelector(selectGasBlockState, (state: State) => state.pathElements);

export const getGasBlocks = createSelector(selectGasBlockState, (state: State) => state.pathGasBlocks);

export const getGraphConfigs = createSelector(selectGasBlockState, (state: State) => state.graphConfig);

export const getOrderedParts = createSelector(selectGasBlockState, (state: State) => state.orderedParts);

export const getPathPoints = createSelector(selectGasBlockState, (state: State) => state.pathPoints);

export const getTolerances = createSelector(selectGasBlockState, (state: State) => state.tolerances);

export const getBlockTolerance = createSelector(selectGasBlockState, (state: State) => state.blockTolerance);

export const getExportConfig = createSelector(selectGasBlockState, (state: State) => state.exportConfig);

export const getIsExporting = createSelector(selectGasBlockState, (state: State) => state.isExporting);

export const getDisplayOnlyPath = createSelector(selectGasBlockState, (state: State) => state.displayOnlyPath);

export const getLastOriginGasBlock = createSelector(selectGasBlockState, (state: State) => state.lastOriginGasblock);

export const getDisplayOnlyPathOrigin = createSelector(
  selectGasBlockState,
  (state: State) => state.displayOnlyPathOrigin
);

// Calculated selectors

export const getIsActive = createSelector(getPathKeys, (parts: Array<string>): boolean => parts.length > 1);

export const getGraphData = createSelector(
  getPathKeys,
  getElements,
  getGasBlocks,
  getGraphConfigs,
  getBlockedKeys,
  fromCore.getThresholds,
  (
    pathKeys: Array<string>,
    elements: Array<PathElement>,
    gasBlocks: Array<PathGasBlock>,
    graphConfig: GraphConfig,
    closedPaths: Array<ClosedPath>,
    thresholds: Thresholds
  ) => {
    const properties = JSON.parse(
      JSON.stringify(graphConfig.data.map(property => thresholds.output.find(prop => prop.name === property)))
    );
    properties.forEach(property => {
      if (!property?.unit) {
        property.unit = 'sans unité';
      }
    });

    if (!properties.length) {
      return {
        graphs: [
          {
            tag: 'Invalide',
            property: {
              id: ThresholdCategory.OUTPUT,
              displayName: 'Invalide',
              unit: 'Invalide',
              name: '',
              highLimit: 9999,
              lowLimit: -9999
            },
            metrics: [],
            color: '#F5FF02',
            equipments: [],
            pipes: [],
            length: 0
          } as BlockGraph
        ],
        axes: []
      };
    }

    const equipments = elements.map(element => {
      return {
        length: element.length,
        name: element.name,
        ouvrage: element.ouvrage,
        key: element.key,
        measures: properties.map(property => element.properties[property.name])
      } as PathEquipment;
    });
    const blocks = [];
    gasBlocks.forEach(block => {
      blocks.push({
        ...block,
        measures: properties.map(property => block.properties?.[property.name])
      });
    });

    return DataChartHelper.formatChartData(properties, pathKeys, equipments, blocks, graphConfig, closedPaths);
  }
);

export const getSplitFavoritePath = createSelector(fromCore.getFavoritePaths, (favoritePaths: Array<FavoritePath>) => {
  return favoritePaths.map(path => ({ ...path, points: path.pointList?.split(',') || [] }));
});

export const getActiveFavoritePath = createSelector(
  getSplitFavoritePath,
  getPathPoints,
  getGraphConfigs,
  fromCore.getUser,
  (favoritePaths: Array<FavoritePath>, pathPoints: Array<PartData>, configs: GraphConfig, user: User) => {
    const filteredPath = favoritePaths?.filter(favorite => favorite.isPublic || favorite.userEmail === user.email);
    for (let f = 0; f < filteredPath?.length; f++) {
      const path = filteredPath[f];
      let isSame = path.points.length === pathPoints?.length;
      for (let p = 0; p < pathPoints?.length; p++) {
        const point = pathPoints[p];
        if (!path.points.includes('' + point.key)) {
          isSame = false;
        }
      }
      isSame = isSame && path.properties.length === configs.data?.length;
      for (let d = 0; d < configs.data?.length; d++) {
        const data = configs.data[d];
        if (!path.properties.includes(data)) {
          isSame = false;
        }
      }
      isSame = isSame && path.isGasBlock === configs.isBlock;
      if (isSame) {
        return path;
      }
    }
    return null;
  }
);

export const getIsGasBlockPossible = createSelector(
  getGasBlocks,
  getGraphConfigs,
  fromCore.getThresholds,
  (gasBlocks: Array<PathGasBlock>, graphConfig: GraphConfig, thresholds: Thresholds) => {
    const availableProperties = [];
    gasBlocks?.forEach(block => {
      if (block.properties) {
        Object.keys(block.properties).forEach(prop => {
          if (!availableProperties.includes(prop)) {
            availableProperties.push(prop);
          }
        });
      }
    });
    const gasProperties = thresholds.output.filter(prop => availableProperties.includes(prop.name));

    if (gasProperties?.length) {
      let allIncluded = true;
      graphConfig.data.forEach(prop => {
        allIncluded = allIncluded && gasProperties.map(prop => prop.name).includes(prop);
      });
      return allIncluded;
    } else {
      return false;
    }
  }
);

export const getSortedFavoritePaths = createSelector(getSplitFavoritePath, (favoritesPaths: Array<FavoritePath>) => {
  const copy = JSON.parse(JSON.stringify(favoritesPaths));
  return copy
    ? copy.sort((a, b) => DateTime.fromISO(b.creationDate).toMillis() - DateTime.fromISO(a.creationDate).toMillis())
    : [];
});
